/**
 * Client API pour l'interface d'administration REMA
 * 
 * Ce fichier contient toutes les fonctions pour communiquer avec le backend API.
 * Il utilise axios pour les requêtes HTTP et gère l'authentification via JWT tokens.
 * 
 * Structure:
 * - Configuration de l'instance axios avec intercepteurs
 * - Gestion des tokens d'authentification
 * - Services API pour chaque ressource (Programme, Infos, Settings, etc.)
 * - Système de notifications toast
 */

import axios from "axios";

// ============================================================================
// CONFIGURATION DE L'API
// ============================================================================

/**
 * Détermine automatiquement l'URL de base de l'API
 * Cette fonction essaie plusieurs méthodes pour trouver l'URL de l'API
 * en commençant par la plus fiable (variable d'environnement)
 * 
 * @returns {string} L'URL de base de l'API
 */
function guessApiBase() {
  // Première méthode: utiliser la variable d'environnement si elle existe
  // C'est la méthode la plus fiable car elle est configurée par l'équipe
  if (process.env.REACT_APP_API) {
    let apiUrl = process.env.REACT_APP_API;
    // Supprimer les slashs à la fin de l'URL pour éviter les doubles slashs
    while (apiUrl.endsWith("/")) {
      apiUrl = apiUrl.slice(0, -1);
    }
    return apiUrl;
  }
  
  // Deuxième méthode: essayer de construire l'URL à partir de la page actuelle
  // Cela permet d'accéder à l'API sur le même réseau (LAN)
  try {
    const protocol = window.location.protocol; // ex: "http:" ou "https:"
    const hostname = window.location.hostname; // ex: "192.168.1.100"
    return protocol + "//" + hostname + ":4000";
  } catch (error) {
    // Si on ne peut pas accéder à window.location, utiliser localhost
    return "http://localhost:4000";
  }
}

// ============================================================================
// GESTION DES TOKENS D'AUTHENTIFICATION
// ============================================================================

const TOKEN_KEY = "auth_token";

/**
 * Récupère le token JWT depuis le localStorage
 * @returns {string} Token ou chaîne vide si non trouvé
 */
function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

/**
 * Stocke ou supprime le token JWT dans le localStorage
 * @param {string} t - Token à stocker (vide pour supprimer)
 */
function setToken(t) {
  try {
    t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

/**
 * Supprime le token JWT du localStorage
 */
function clearToken() {
  setToken("");
}

// ============================================================================
// INSTANCE AXIOS AVEC INTERCEPTEURS
// ============================================================================

/**
 * Instance axios configurée pour communiquer avec l'API backend
 * - baseURL: URL de l'API détectée automatiquement
 * - timeout: 10 secondes
 * - headers: JSON par défaut
 */
export const api = axios.create({
  baseURL: guessApiBase(),
  timeout: 10000,
  headers: { "Content-Type": "application/json" },
});

/**
 * Intercepteur de requêtes: Ajoute le token JWT dans les headers Authorization
 * Cette fonction est appelée automatiquement avant chaque requête vers l'API
 * Elle vérifie si un token existe et l'ajoute dans les headers si nécessaire
 */
api.interceptors.request.use((configuration) => {
  const token = getToken();
  // Si un token existe, l'ajouter dans le header Authorization
  // Le format standard est "Bearer <token>"
  if (token) {
    configuration.headers["Authorization"] = "Bearer " + token;
  }
  return configuration;
});

/**
 * Intercepteur de réponses: Gère les erreurs 401 (non autorisé)
 * Cette fonction est appelée automatiquement après chaque réponse de l'API
 * Si l'API renvoie une erreur 401, cela signifie que le token est invalide ou expiré
 * Dans ce cas, on déconnecte l'utilisateur et on le redirige vers la page de login
 */
api.interceptors.response.use(
  // Premier paramètre: traitement des réponses réussies
  (reponse) => {
    // On ne fait rien de spécial, on retourne la réponse telle quelle
    return reponse;
  },
  // Deuxième paramètre: traitement des erreurs
  (erreur) => {
    // Récupérer le code de statut HTTP de l'erreur
    const statut = erreur?.response?.status;
    
    // Si le statut est 401 (Non autorisé), le token n'est plus valide
    if (statut === 401) {
      // Supprimer le token invalide
      clearToken();
      
      // Rediriger l'utilisateur vers la page de login
      try {
        // Vérifier qu'on n'est pas déjà sur la page de login pour éviter une boucle
        if (window.location.pathname !== "/login") {
          window.location.assign("/login");
        }
      } catch (erreurRedirection) {
        // Si la redirection échoue, on continue quand même
        // L'application restera bloquée mais ne plantera pas
      }
    }
    
    // Propager l'erreur pour que le code appelant puisse la gérer
    return Promise.reject(erreur);
  },
);

// ============================================================================
// UTILITAIRES
// ============================================================================

/**
 * Extrait les données de la réponse axios
 * Les réponses d'axios contiennent beaucoup d'informations (headers, status, etc.)
 * Cette fonction récupère uniquement les données utiles qui sont dans la propriété "data"
 * 
 * @param {Object} reponseAxios - La réponse complète de axios
 * @returns {any} Les données de la réponse (la propriété data)
 */
async function ok(reponseAxios) {
  // La réponse axios a la structure: { data: {...}, status: 200, headers: {...} }
  // On ne retourne que la partie "data" qui contient les informations utiles
  return reponseAxios.data;
}

// ============================================================================
// SERVICES API - HEALTH
// ============================================================================

/**
 * Service pour vérifier l'état de santé de l'API
 */
export const Health = {
  /**
   * Ping l'API pour vérifier si elle est en ligne
   * @returns {Promise<Object>} Réponse de santé
   */
  async ping() {
    return ok(await api.get("/health"));
  },
  
  /**
   * Récupère la version de l'API
   * @returns {Promise<Object>} Version de l'API
   */
  async version() {
    return ok(await api.get("/version"));
  },
};

// ============================================================================
// SERVICES API - AUTHENTIFICATION
// ============================================================================

/**
 * Service pour l'authentification des utilisateurs
 */
export const Auth = {
  /**
   * Connecte un utilisateur avec username/password
   * @param {string} username - Nom d'utilisateur
   * @param {string} password - Mot de passe
   * @returns {Promise<Object>} Données utilisateur avec token
   * @throws {Error} Si aucun token n'est retourné
   */
  async login(username, password) {
    const data = await ok(
      await api.post("/auth/login", { username, password }),
    );
    const token = data?.token || "";
    if (!token) throw new Error("NO_TOKEN");
    setToken(token);
    return data;
  },
  
  /**
   * Déconnecte l'utilisateur en supprimant le token
   */
  logout() {
    clearToken();
  },
  
  /**
   * Récupère le token JWT actuel
   * @returns {string} Token JWT
   */
  getToken,
  
  /**
   * Vérifie si un utilisateur est connecté
   * @returns {boolean} True si un token existe
   */
  isAuthenticated() {
    return !!getToken();
  },
};

// ============================================================================
// SERVICES API - PROGRAMME
// ============================================================================

/**
 * Service pour gérer le programme des sessions REMA
 */
export const Programme = {
  /**
   * Récupère toutes les sessions du programme
   * @returns {Promise<Array>} Liste des sessions
   */
  async list() {
    return ok(await api.get("/programme"));
  },
  
  /**
   * Crée une nouvelle session de programme
   * @param {Object} body - Données de la session
   * @returns {Promise<Object>} Session créée
   */
  async create(body) {
    return ok(await api.post("/programme", body));
  },
  
  /**
   * Met à jour une session existante
   * @param {string|number} id - ID de la session
   * @param {Object} body - Données à mettre à jour
   * @returns {Promise<Object>} Session mise à jour
   */
  async update(id, body) {
    return ok(await api.put(`/programme/${id}`, body));
  },
  
  /**
   * Supprime une session
   * @param {string|number} id - ID de la session
   * @returns {Promise<Object>} Confirmation de suppression
   */
  async remove(id) {
    return ok(await api.delete(`/programme/${id}`));
  },
  
  /**
   * Réinitialise le programme aux données par défaut
   * @returns {Promise<Object>} Confirmation de réinitialisation
   */
  async reset() {
    return ok(await api.delete("/programme/reset"));
  },
};

// ============================================================================
// SERVICES API - INFOS PRATIQUES
// ============================================================================

/**
 * Service pour gérer les informations pratiques (hôtels, taxis, etc.)
 */
export const Infos = {
  /**
   * Récupère toutes les infos pratiques
   * @returns {Promise<Array>} Liste des infos
   */
  async list() {
    return ok(await api.get("/infos"));
  },
  
  /**
   * Récupère une info pratique par ID
   * @param {string|number} id - ID de l'info
   * @returns {Promise<Object>} Info demandée
   */
  async get(id) {
    return ok(await api.get(`/infos/${id}`));
  },
  
  /**
   * Crée une nouvelle info pratique
   * @param {Object} body - Données de l'info
   * @returns {Promise<Object>} Info créée
   */
  async create(body) {
    return ok(await api.post("/infos", body));
  },
  
  /**
   * Met à jour une info pratique existante
   * @param {string|number} id - ID de l'info
   * @param {Object} body - Données à mettre à jour
   * @returns {Promise<Object>} Info mise à jour
   */
  async update(id, body) {
    return ok(await api.put(`/infos/${id}`, body));
  },
  
  /**
   * Supprime une info pratique
   * @param {string|number} id - ID de l'info
   * @returns {Promise<Object>} Confirmation de suppression
   */
  async remove(id) {
    return ok(await api.delete(`/infos/${id}`));
  },
};

// ============================================================================
// SERVICES API - PARTICIPANTS PAR SESSION
// ============================================================================

/**
 * Service pour gérer les participants assignés à chaque session de programme
 */
export const People = {
  /**
   * Récupère les participants d'une session de programme
   * @param {string|number} programmeId - ID de la session
   * @returns {Promise<Array>} Liste des participants
   */
  async list(programmeId) {
    return ok(await api.get(`/programme/${programmeId}/people`));
  },
  
  /**
   * Récupère toutes les personnes disponibles
   * @returns {Promise<Array>} Liste de toutes les personnes
   */
  async listAll() {
    return ok(await api.get(`/people`));
  },
  
  /**
   * Crée une nouvelle personne
   * @param {Object} body - Données de la personne
   * @returns {Promise<Object>} Personne créée
   */
  async createPerson(body) {
    return ok(await api.post(`/people`, body));
  },
  
  /**
   * Modifie une personne existante
   * @param {string|number} personId - ID de la personne
   * @param {Object} body - Données de la personne
   * @returns {Promise<Object>} Personne modifiée
   */
  async updatePerson(personId, body) {
    return ok(await api.put(`/people/${personId}`, body));
  },
  
  /**
   * Supprime une personne
   * @param {string|number} personId - ID de la personne
   * @returns {Promise<Object>} Résultat de la suppression
   */
  async removePerson(personId) {
    return ok(await api.delete(`/people/${personId}`));
  },
  
  /**
   * Ajoute un participant à une session
   * @param {string|number} programmeId - ID de la session
   * @param {Object} body - Données du participant
   * @returns {Promise<Object>} Participant créé
   */
  async create(programmeId, body) {
    return ok(await api.post(`/programme/${programmeId}/people`, body));
  },
  
  /**
   * Modifie un participant existant
   * @param {string|number} participantId - ID du participant (programme_people.id)
   * @param {Object} body - Données à modifier
   * @returns {Promise<Object>} Participant modifié
   */
  async update(participantId, body) {
    return ok(await api.put(`/programme-people/${participantId}`, body));
  },

  /**
   * Supprime un participant d'une session
   * @param {string|number} participantId - ID du participant (programme_people.id)
   * @returns {Promise<Object>} Résultat de la suppression
   */
  async remove(participantId) {
    return ok(await api.delete(`/programme-people/${participantId}`));
  },
};

// ============================================================================
// SERVICES API - SETTINGS: JOURS
// ============================================================================

/**
 * Service pour gérer les jours de l'événement
 */
export const SettingsDays = {
  /**
   * Récupère la liste des jours configurés
   * @returns {Promise<Array>} Liste des jours
   */
  async list() {
    return ok(await api.get("/settings/days"));
  },
  
  /**
   * Sauvegarde la configuration des jours
   * @param {Array} days - Liste des jours à sauvegarder
   * @returns {Promise<Object>} Confirmation de sauvegarde
   */
  async save(days) {
    return ok(await api.post("/settings/days", { days }));
  },
};

// ============================================================================
// SERVICES API - SETTINGS: BANNIÈRE PARTENAIRES
// ============================================================================

/**
 * Service pour gérer la bannière des partenaires
 */
export const SettingsPartnersBanner = {
  /**
   * Récupère l'URL de la bannière partenaires
   * @returns {Promise<Object>} URL de la bannière
   */
  async get() {
    return ok(await api.get("/settings/partners-banner"));
  },
  
  /**
   * Met à jour l'URL de la bannière partenaires
   * @param {string} url - Nouvelle URL de la bannière
   * @returns {Promise<Object>} Confirmation de sauvegarde
   */
  async save(url) {
    return ok(await api.post("/settings/partners-banner", { url }));
  },
};

// ============================================================================
// SERVICES API - RÉSEAUX SOCIAUX
// ============================================================================

/**
 * Service pour gérer les liens des réseaux sociaux
 */
export const SocialLinks = {
  /**
   * Récupère tous les liens sociaux
   * @returns {Promise<Array>} Liste des liens sociaux
   */
  async list() {
    return ok(await api.get("/social-links"));
  },
  
  /**
   * Crée un nouveau lien social
   * @param {Object} body - Données du lien (key, label, icon, url, order)
   * @returns {Promise<Object>} Lien créé
   */
  async create(body) {
    return ok(await api.post("/social-links", body));
  },
  
  /**
   * Met à jour un lien social existant
   * @param {string|number} id - ID du lien
   * @param {Object} body - Données à mettre à jour
   * @returns {Promise<Object>} Lien mis à jour
   */
  async update(id, body) {
    return ok(await api.put(`/social-links/${id}`, body));
  },
  
  /**
   * Supprime un lien social
   * @param {string|number} id - ID du lien
   * @returns {Promise<Object>} Confirmation de suppression
   */
  async remove(id) {
    return ok(await api.delete(`/social-links/${id}`));
  },
  
  /**
   * Réinitialise les liens sociaux aux valeurs par défaut
   * @returns {Promise<Object>} Confirmation de réinitialisation
   */
  async reset() {
    return ok(await api.post("/social-links/reset"));
  },
};

// ============================================================================
// SERVICES API - LIEUX
// ============================================================================

/**
 * Service pour gérer les lieux (emplacements) de l'événement
 */
export const Locations = {
  /**
   * Récupère tous les lieux
   * @returns {Promise<Array>} Liste des lieux
   */
  async list() {
    return ok(await api.get("/locations"));
  },
  
  /**
   * Crée un nouveau lieu
   * @param {Object} body - Données du lieu (name, maps_url, ord)
   * @returns {Promise<Object>} Lieu créé
   */
  async create(body) {
    return ok(await api.post("/locations", body));
  },
  
  /**
   * Met à jour un lieu existant
   * @param {string|number} id - ID du lieu
   * @param {Object} body - Données à mettre à jour
   * @returns {Promise<Object>} Lieu mis à jour
   */
  async update(id, body) {
    return ok(await api.put(`/locations/${id}`, body));
  },
  
  /**
   * Supprime un lieu
   * @param {string|number} id - ID du lieu
   * @returns {Promise<Object>} Confirmation de suppression
   */
  async remove(id) {
    return ok(await api.delete(`/locations/${id}`));
  },
};

// ============================================================================
// SERVICES API - CATÉGORIES D'INFOS
// ============================================================================

/**
 * Service pour gérer les catégories d'informations pratiques
 */
export const InfoCategories = {
  /**
   * Récupère toutes les catégories d'infos
   * @returns {Promise<Array>} Liste des catégories
   */
  async list() {
    return ok(await api.get("/info-categories"));
  },
  
  /**
   * Crée une nouvelle catégorie
   * @param {Object} body - Données de la catégorie (key, label, ord)
   * @returns {Promise<Object>} Catégorie créée
   */
  async create(body) {
    return ok(await api.post("/info-categories", body));
  },
  
  /**
   * Met à jour une catégorie existante
   * @param {string|number} id - ID de la catégorie
   * @param {Object} body - Données à mettre à jour
   * @returns {Promise<Object>} Catégorie mise à jour
   */
  async update(id, body) {
    return ok(await api.put(`/info-categories/${id}`, body));
  },
  
  /**
   * Supprime une catégorie
   * @param {string|number} id - ID de la catégorie
   * @returns {Promise<Object>} Confirmation de suppression
   */
  async remove(id) {
    return ok(await api.delete(`/info-categories/${id}`));
  },
  
  /**
   * Réinitialise les catégories aux valeurs par défaut
   * @returns {Promise<Object>} Confirmation de réinitialisation
   */
  async reset() {
    return ok(await api.post("/info-categories/reset"));
  },
};

// ============================================================================
// SYSTÈME DE NOTIFICATIONS TOAST
// ============================================================================

/**
 * Système de notifications toast pour afficher des messages à l'utilisateur
 * Utilise le pattern observer/subscriber pour notifier les composants
 */
const listeners = new Set();

/**
 * Émet un événement toast à tous les abonnés
 * Cette fonction appelle toutes les fonctions qui se sont abonnées aux notifications
 * Chaque abonné recevra l'événement et pourra afficher un message à l'utilisateur
 * 
 * @param {Object} evenement - L'événement à émettre avec type et message
 */
function emit(evenement) {
  // Parcourir toutes les fonctions abonnées (les écouteurs)
  for (const fonctionEcouteur of listeners) {
    try {
      // Appeler la fonction avec l'événement
      fonctionEcouteur(evenement);
    } catch (erreur) {
      // Si un écouteur plante, on continue avec les autres
      // On ne fait rien car une erreur d'affichage ne doit pas casser l'application
    }
  }
}

/**
 * API publique du système de toast
 * Ces fonctions permettent d'afficher des notifications à l'utilisateur
 */
export const toast = {
  /**
   * S'abonne aux événements toast
   * Cette fonction permet à un composant de recevoir les notifications
   * 
   * @param {Function} fonctionEcouteur - Fonction appelée quand un toast est émis
   * @returns {Function} Fonction à appeler pour se désabonner
   */
  subscribe(fonctionEcouteur) {
    // Ajouter la fonction à la liste des écouteurs
    listeners.add(fonctionEcouteur);
    
    // Retourner une fonction qui permet de se désabonner
    // Quand cette fonction est appelée, on supprime l'écouteur de la liste
    return function seDesabonner() {
      listeners.delete(fonctionEcouteur);
    };
  },
  
  /**
   * Affiche un toast de succès
   * Cette fonction affiche un message vert pour confirmer une action réussie
   * 
   * @param {string} message - Le message de succès à afficher
   */
  success(message) {
    // Afficher le message dans la console pour le débogage
    console.log("[OK]", message);
    
    // Émettre l'événement à tous les écouteurs
    emit({ type: "success", message: message });
  },
  
  /**
   * Affiche un toast d'erreur
   * Cette fonction affiche un message rouge pour signaler une erreur
   * 
   * @param {string} message - Le message d'erreur à afficher
   */
  error(message) {
    // Afficher l'erreur dans la console pour le débogage
    console.error("[ERR]", message);
    
    // Émettre l'événement à tous les écouteurs
    emit({ type: "error", message: message });
  },
};
